import type { Logger } from '@vp/ubik-logging'

import type { NewWork, Work } from '../types/Work'
import type { WorkId } from '../types/WorkId'
import { REQUESTOR } from '../utils/constants'

const baseUrl = 'https://workentityservice.design.vpsvc.com/api/v1/works'

export const getWork = async (cartTenant: string, workId: WorkId, authorizationHeader: string, logger: Logger): Promise<Work> => {
  try {
    if (!workId) throw Error('no work id')
    const queryParams = new URLSearchParams({
      from: REQUESTOR,
      tenant: cartTenant,
    })
    const url = `${baseUrl}/${workId}?${queryParams.toString()}`
    const headers = { Authorization: authorizationHeader }
    const request = new Request(url, {
      headers,
    })

    const response = await fetch(request)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as Work
    return data
  } catch (error) {
    logger.error('Unable to retrieve work', error as Error, { workId })
    throw error
  }
}

export const saveWork = async (
  cartTenant: string,
  work: Work,
  isCareAgent: boolean,
  ownerId: string,
  authorizationHeader: string,
  logger: Logger,
): Promise<Work> => {
  try {
    const queryParams = new URLSearchParams({
      ownerId: isCareAgent ? ownerId : '',
      tenant: cartTenant,
      from: REQUESTOR,
    })

    // Remove empty ownerId param if not care agent
    if (!isCareAgent) {
      queryParams.delete('ownerId')
    }

    const url = `${baseUrl}/${work.workId}/update?${queryParams.toString()}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: authorizationHeader,
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(work),
    })

    if (!response.ok) {
      throw new Error(`Failed to save work: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as Work

    return data
  } catch (error) {
    logger.error('Unable to save work in WES', error as Error, { workId: work.workId, work, isCareAgent, ownerId })
    throw error
  }
}

export const insertWork = async (
  cartTenant: string,
  work: NewWork,
  isCareAgent: boolean,
  ownerId: string,
  authorizationHeader: string,
  logger: Logger,
): Promise<Work> => {
  try {
    const queryParams = new URLSearchParams({
      ownerId: isCareAgent ? ownerId : '', // The id of the customer who made the original design
      tenant: cartTenant,
      from: REQUESTOR,
    })

    // Remove empty ownerId param if not care agent
    if (!isCareAgent) {
      queryParams.delete('ownerId')
    }

    const url = `${baseUrl}?${queryParams.toString()}`
    const headers = { Authorization: authorizationHeader, 'Content-Type': 'application/json' }
    const request = new Request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(work),
    })

    const response = await fetch(request)
    if (!response.ok) {
      throw new Error(`Failed to insert work: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as Work
    return data
  } catch (error) {
    logger.error('Unable to insert work', error as Error, { work, isCareAgent, ownerId })
    throw error
  }
}
